import type { DayReport } from "../types/api";
import { getFunctionUrl } from "../config";
import {
  type ArchivedAssetLink,
  type AssetUrlCacheEntry,
  resolveAssetUrls,
} from "./assetUrlResolver";
import { getAuthToken } from "./auth";
import { getSupabaseClient } from "./supabase";

const ARCHIVE_TIMEOUT_MS = 15_000;
const cache = new Map<string, AssetUrlCacheEntry>();

export async function fetchArchivedAssetLinks(dayReportId: string): Promise<ArchivedAssetLink[]> {
  const token = await getAuthToken();
  if (!token) return [];

  const controller = new AbortController();
  const timeoutId = globalThis.setTimeout(() => controller.abort(), ARCHIVE_TIMEOUT_MS);
  try {
    const response = await fetch(
      getFunctionUrl(
        "dbvdw-api",
        `/reports/${encodeURIComponent(dayReportId)}/asset-links`,
      ),
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        signal: controller.signal,
      },
    );
    if (!response.ok) {
      throw new Error(`Archive asset links unavailable (HTTP ${response.status})`);
    }
    const payload: unknown = await response.json();
    if (!Array.isArray(payload)) return [];
    return payload.flatMap((item) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) return [];
      const record = item as Record<string, unknown>;
      if (
        record.source !== "dropbox" ||
        typeof record.report_asset_id !== "string"
      ) {
        return [];
      }
      const url = typeof record.url === "string" && record.url ? record.url : null;
      const storagePurged = record.storage_purged === true;
      if (!url && !storagePurged) return [];
      return [{
        report_asset_id: record.report_asset_id,
        url,
        preview_path: typeof record.preview_path === "string" && record.preview_path
          ? record.preview_path
          : undefined,
        storage_purged: storagePurged,
        archive_error: record.archive_error === "archive_source_unavailable"
          ? "archive_source_unavailable" as const
          : undefined,
        source: "dropbox" as const,
      }];
    });
  } finally {
    globalThis.clearTimeout(timeoutId);
  }
}

export interface SignedAssetUrlState {
  urls: Map<string, string>;
  archiveUnavailablePaths: Set<string>;
}

export async function signedAssetUrlState(
  report: DayReport,
): Promise<SignedAssetUrlState> {
  const archiveUnavailablePaths = new Set<string>();
  const urls = await resolveAssetUrls(
    report,
    {
      fetchArchivedAssetLinks,
      signStorageUrls: async (paths) => {
        const { data, error } = await getSupabaseClient().storage.from("report-assets")
          .createSignedUrls(paths, 3600);
        if (error || !Array.isArray(data)) return [];
        return data.map((item) => ({
          path: item.path,
          signedUrl: item.signedUrl,
          error: item.error,
        }));
      },
      onArchiveError: (error) => {
        console.warn(
          "Dropbox-fotolinks konden niet worden opgehaald; Supabase Storage blijft fallback",
          error,
        );
      },
      onArchiveUnavailable: (path) => archiveUnavailablePaths.add(path),
    },
    cache,
  );
  return { urls, archiveUnavailablePaths };
}

export async function signedAssetUrls(
  report: DayReport,
): Promise<Map<string, string>> {
  return (await signedAssetUrlState(report)).urls;
}
